import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../components';

export function DirectoryFilterPopover({
  children,
  activeCount = 0,
  onReset,
  label = 'Filters',
}: {
  children: ReactNode;
  activeCount?: number;
  onReset: () => void;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const instanceId = useId();

  useEffect(() => {
    if (!open) return undefined;

    const closeOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const closeForAnotherMenu = (event: Event) => {
      const otherId = (event as CustomEvent<string>).detail;
      if (otherId !== instanceId) setOpen(false);
    };

    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('nuramend:directory-filter-open', closeForAnotherMenu);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('nuramend:directory-filter-open', closeForAnotherMenu);
    };
  }, [instanceId, open]);

  const toggle = () => {
    setOpen((current) => {
      const next = !current;
      if (next) window.dispatchEvent(new CustomEvent('nuramend:directory-filter-open', { detail: instanceId }));
      return next;
    });
  };

  return <div ref={rootRef} className={`nds-directory-filter ${open ? 'is-open' : ''}`.trim()}>
    <button
      type="button"
      className="button nds-directory-filter-trigger"
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={toggle}
    >
      <Icon name="filter" />
      {label}
      {activeCount > 0 && <span className="nds-directory-filter-count">{activeCount}</span>}
    </button>
    {open && <div className="nds-directory-filter-popover" role="dialog" aria-label={`${label} options`}>
      <div className="nds-directory-filter-fields">{children}</div>
      <button type="button" className="button small nds-directory-filter-reset" onClick={onReset}><Icon name="refresh" />Reset</button>
    </div>}
  </div>;
}
